// Small pieces the building screens share: the completion ring, due / status chips, the side drawer and
// the emergency "who to call" strip at the top of a property page (Task H).
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { Sheet } from '../ui';
import { useCan } from '../../hooks/usePermission';
import { buildingApi, DUE_COLOUR, STATUS_COLOUR, telHref, type DueState, type SectionStatus } from '../../lib/building';
import { formatPhone } from '../../lib/phone';

/** Completion ring (prototype .ring): a conic gradient with the percentage in the middle. */
export function Ring({ pct, size = 44, colour = 'var(--color-bronze-500)', label }: { pct: number; size?: number; colour?: string; label?: string }) {
  const inner = size - 8;
  return (
    <div className="relative grid shrink-0 place-items-center rounded-full" style={{ width: size, height: size, background: `conic-gradient(${colour} ${pct * 3.6}deg, #ece8e1 0deg)` }} aria-label={`${pct}% complete`}>
      <div className="grid place-items-center rounded-full bg-white text-center" style={{ width: inner, height: inner }}>
        <span className="text-[11px] font-semibold leading-none text-ink-800">{pct}%</span>
        {label && <span className="text-[8.5px] uppercase tracking-wider text-ink-400">{label}</span>}
      </div>
    </div>
  );
}

export function Chip({ colour, children, title }: { colour: string; children: ReactNode; title?: string }) {
  return (
    <span title={title} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium leading-none" style={{ background: `${colour}1a`, color: colour }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: colour }} />{children}
    </span>
  );
}
export const DueChip = ({ due }: { due: DueState }) => <Chip colour={DUE_COLOUR[due.key]}>{due.label}</Chip>;
export const StatusChip = ({ status, label }: { status: SectionStatus; label: string }) => <Chip colour={STATUS_COLOUR[status]}>{label}</Chip>;

/** A record drawer: the Sheet, sitting under any dialog it opens (z 30 against the Modal's 40). */
export function Drawer({ title, subtitle, accent, onClose, children, footer }: { title: ReactNode; subtitle?: ReactNode; accent?: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return <Sheet open onClose={onClose} title={title} subtitle={subtitle} accent={accent} footer={footer} size="lg" z={30}>{children}</Sheet>;
}

/** The emergency numbers for a building, as a strip of tap-to-call buttons (top of the property page). */
export function EmergencyStrip({ propertyId }: { propertyId: string }) {
  const staff = useCan('buildingRecord.read');
  const resident = useCan('residents.read');
  const allowed = staff || resident;
  const q = useQuery({ queryKey: ['who-to-call', propertyId], queryFn: () => buildingApi.whoToCall(propertyId), enabled: allowed });
  const list = (q.data?.entries ?? []).filter((e) => e.emergency && e.phone);
  if (!allowed || !list.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-red-200 bg-red-50/60 px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-red-700"><Phone className="h-3.5 w-3.5" /> Emergency — who to call</div>
      <div className="flex gap-2 overflow-x-auto pb-0.5">
        {list.map((e, i) => (
          <a key={`${e.role}-${i}`} href={telHref(e.phone!)} className="shrink-0 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-[12px] leading-tight text-ink-800 transition hover:border-red-300">
            <span className="block text-[10.5px] text-red-700">{e.role}</span>
            <span className="font-semibold tabular-nums">{formatPhone(e.phone)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
